/**
 * VersionsTab — Version history and field-level diff for all 25 primitives
 *
 * Generalizes the TemplateVersionHistory pattern for any entity type.
 * Uses the audit trail to reconstruct version snapshots and show diffs.
 *
 * Since FERP is append-only, every UPDATE audit record represents a new version.
 * The version field on the entity tracks the current version number.
 */

import { useState, useMemo } from 'react';
import {
  Stack,
  Text,
  Group,
  Badge,
  Table,
  Center,
  Loader,
  Alert,
  Divider,
  Select,
  Timeline,
  ThemeIcon,
} from '@mantine/core';
import {
  IconGitCompare,
  IconCirclePlus,
  IconEdit,
} from '@tabler/icons-react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useTenantId } from '@/auth';
import type { AuditTrailEntry, AuditTrailResponse } from '@/generated/graphql';
import type { PrimitiveItem } from '@/hooks/usePrimitiveData';

// ============================================================================
// GRAPHQL QUERY
// ============================================================================

const AUDIT_TRAIL_QUERY = gql`
  query VersionsAuditTrail($filter: AuditTrailFilter!) {
    getAuditTrail(filter: $filter) {
      entries {
        auditId
        action
        entityId
        entityType
        userId
        userName
        timestamp
        tamperDetected
      }
      totalCount
    }
  }
`;

// ============================================================================
// HELPERS
// ============================================================================

function formatTimestamp(ts: string): string {
  try {
    return new Date(ts).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return ts;
  }
}

// ============================================================================
// TYPES
// ============================================================================

interface VersionEntry {
  versionNumber: number;
  timestamp: string;
  userId: string;
  userEmail?: string | null;
  action: string;
  auditId: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

interface VersionsTabProps {
  entity: PrimitiveItem | null;
  primitiveType: string;
}

export function VersionsTab({ entity, primitiveType }: Readonly<VersionsTabProps>) {
  const tenantId = useTenantId() ?? '';
  const entityId = entity?.id as string | undefined;
  const currentVersion = (entity?.version as number) ?? 1;
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);

  const { data, loading, error } = useQuery<{ getAuditTrail: AuditTrailResponse }>(AUDIT_TRAIL_QUERY, {
    variables: {
      filter: {
        tenantId,
        entityId,
        entityType: primitiveType.toUpperCase(),
        pageSize: 100,
      },
    },
    skip: !entityId || !tenantId,
    fetchPolicy: 'cache-and-network',
  });

  const records: AuditTrailEntry[] = data?.getAuditTrail?.entries ?? [];

  // Build version list from audit records
  const versions = useMemo<VersionEntry[]>(() => {
    const mutationRecords = records
      .filter((r: AuditTrailEntry) => !r.tamperDetected && (r.action === 'CREATE' || r.action === 'UPDATE'))
      .sort((a: AuditTrailEntry, b: AuditTrailEntry) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );

    return mutationRecords.map((r: AuditTrailEntry, idx: number) => ({
      versionNumber: idx + 1,
      timestamp: r.timestamp,
      userId: r.userId,
      userEmail: r.userName,
      action: r.action,
      auditId: r.auditId,
    }));
  }, [records]);

  if (!entityId) {
    return (
      <Center py="xl">
        <Text c="dimmed" size="sm">Select an entity to view version history.</Text>
      </Center>
    );
  }

  if (loading && versions.length === 0) {
    return (
      <Center py="xl">
        <Stack align="center" gap="sm">
          <Loader size="sm" />
          <Text c="dimmed" size="sm">Loading version history...</Text>
        </Stack>
      </Center>
    );
  }

  if (error) {
    return (
      <Alert color="red" title="Version History Unavailable">
        {error.message}
      </Alert>
    );
  }

  if (versions.length === 0) {
    // Show basic version info from entity fields
    return (
      <Stack gap="md" py="md">
        <Group gap="xs">
          <IconGitCompare size={16} />
          <Text size="sm" fw={600}>Version History</Text>
        </Group>
        <Table withTableBorder>
          <Table.Tbody>
            <Table.Tr>
              <Table.Td fw={600} w={120}>Current Version</Table.Td>
              <Table.Td>
                <Badge size="sm" variant="light" color="blue">v{currentVersion}</Badge>
              </Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td fw={600}>Created</Table.Td>
              <Table.Td>
                <Text size="sm">{entity?.createdAt ? formatTimestamp(entity.createdAt as string) : '—'}</Text>
              </Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td fw={600}>Last Updated</Table.Td>
              <Table.Td>
                <Text size="sm">{entity?.updatedAt ? formatTimestamp(entity.updatedAt as string) : '—'}</Text>
              </Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td fw={600}>Created By</Table.Td>
              <Table.Td>
                <Text size="sm">{(entity?.createdBy as string) ?? '—'}</Text>
              </Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
        <Text size="xs" c="dimmed">
          Detailed version diff will be available when audit trail records include field-level change tracking.
        </Text>
      </Stack>
    );
  }

  return (
    <Stack gap="md" py="md">
      <Group justify="space-between">
        <Group gap="xs">
          <IconGitCompare size={16} />
          <Text size="sm" fw={600}>Version History</Text>
        </Group>
        <Group gap="xs">
          <Badge size="sm" variant="light" color="blue">
            Current: v{currentVersion}
          </Badge>
          <Badge size="xs" variant="light">
            {versions.length} version{versions.length !== 1 ? 's' : ''}
          </Badge>
        </Group>
      </Group>

      {/* Version selector for comparison */}
      {versions.length > 1 && (
        <Select
          label="Compare with version"
          placeholder="Select a version to compare"
          data={versions
            .filter(v => v.versionNumber !== currentVersion)
            .map(v => ({
              value: String(v.versionNumber),
              label: `v${v.versionNumber} — ${formatTimestamp(v.timestamp)} by ${v.userEmail ?? v.userId}`,
            }))}
          value={selectedVersion}
          onChange={setSelectedVersion}
          clearable
          size="xs"
        />
      )}

      <Divider />

      {/* Version timeline */}
      <Timeline active={versions.length - 1} bulletSize={24} lineWidth={2}>
        {[...versions].reverse().map((version) => {
          const isCreate = version.action === 'CREATE';
          const isCurrent = version.versionNumber === currentVersion;

          return (
            <Timeline.Item
              key={version.auditId}
              bullet={
                <ThemeIcon
                  size={24}
                  variant={isCurrent ? 'filled' : 'light'}
                  color={isCreate ? 'green' : 'blue'}
                >
                  {isCreate ? <IconCirclePlus size={12} /> : <IconEdit size={12} />}
                </ThemeIcon>
              }
              title={
                <Group gap="xs">
                  <Text size="sm" fw={isCurrent ? 700 : 400}>
                    v{version.versionNumber}
                  </Text>
                  {isCurrent && (
                    <Badge size="xs" variant="filled" color="blue">Current</Badge>
                  )}
                  {isCreate && (
                    <Badge size="xs" variant="light" color="green">Created</Badge>
                  )}
                </Group>
              }
            >
              <Text size="xs" c="dimmed">
                {formatTimestamp(version.timestamp)}
              </Text>
              <Text size="xs" c="dimmed">
                {version.userEmail ?? version.userId}
              </Text>
            </Timeline.Item>
          );
        })}
      </Timeline>
    </Stack>
  );
}
